import { TextField, Divider, Box } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { Resource } from '@setvi/shared/interfaces';
import SLabel from '@setvi/shared/components/sui/slabel';
import { resourceView } from '@setvi/shared/utils/viewer';
import {
  SText,
  SBase,
  SButton,
  SETVIImage,
  SFormikSelect
} from '@setvi/shared/components';

import { GetResourceTypeThumbnail } from 'Utils/getResourceTypeThumbnail';

interface LocalPrinterDialogProps {
  resources: Partial<Resource>[];
  onClose?: () => void;
  handleSubmit: (quantity: number, paperType: string) => void;
}

const paperTypes = [
  { value: '1', label: 'Paper A', id: 1 },
  { value: '2', label: 'Paper B', id: 2 },
  { value: '3', label: 'Paper C', id: 3 }
];

const validationSchema = Yup.object({
  quantity: Yup.number()
    .typeError('Paper Quantity is required')
    .required('Paper Quantity is required')
    .min(1, 'Must be at least 1'),
  paperType: Yup.string().required('Paper Type is required')
});

const LocalPrinterDialog = ({
  resources,
  onClose,
  handleSubmit
}: LocalPrinterDialogProps) => (
  <SBase width={400} header={{ title: 'Send Email', onClose, divider: true }}>
    {resources?.length === 1 ? (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gridGap={8}>
        {resources?.[0]?.ThumbURL && (
          <SETVIImage
            radius={8}
            alt="image"
            src={
              resources?.[0]?.ThumbURLWithSas ||
              resources?.[0]?.ThumbURL ||
              GetResourceTypeThumbnail(resources?.[0]?.ResourceTypeID)
            }
          />
        )}

        <SText title={resources?.[0]?.Name} weight="bold" />

        <SButton
          variant="outlined"
          onClick={() =>
            resourceView({
              name: resources?.[0]?.Name,
              resourceTypeId: resources?.[0]?.ResourceTypeID,
              resourceId: resources?.[0]?.ResourceID
            })
          }>
          Open Resource
        </SButton>
      </Box>
    ) : (
      resources?.map(r => (
        <Box
          gridGap={8}
          marginBottom={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          key={r?.ID}>
          <Box gridGap={8} display="flex" alignItems="center">
            <SETVIImage
              radius={8}
              alt="image"
              width={75}
              src={
                r?.ThumbURLWithSas ||
                r?.ThumbURL ||
                GetResourceTypeThumbnail(r?.ResourceTypeID)
              }
            />

            <SText title={r?.Name} weight="semibold" />
          </Box>

          <SButton
            variant="outlined"
            onClick={() =>
              resourceView({
                name: resources?.[0]?.Name,
                resourceTypeId: resources?.[0]?.ResourceTypeID,
                resourceId: resources?.[0]?.ResourceID
              })
            }>
            Open
          </SButton>
        </Box>
      ))
    )}

    <Divider style={{ margin: '16px 0' }} />

    <Formik
      initialValues={{ quantity: 1, paperType: '' }}
      validationSchema={validationSchema}
      onSubmit={values => handleSubmit(values.quantity, values.paperType)}>
      {({ values, errors, touched, handleChange, isValid, dirty }) => (
        <Form style={{ width: '100%', textAlign: 'left' }}>
          <Box>
            <SLabel
              title="Paper Quantity"
              variant="body2"
              style={{
                marginBottom: '-8px'
              }}
            />
            <TextField
              fullWidth
              required
              type="number"
              margin="normal"
              name="quantity"
              variant="outlined"
              value={values.quantity}
              onChange={handleChange}
              inputProps={{ min: 1, max: 1000 }}
              error={touched.quantity && Boolean(errors.quantity)}
              helperText={touched.quantity && errors.quantity}
            />
          </Box>

          <SFormikSelect
            fullWidth
            name="paperType"
            label="Paper Type"
            placeholder="Select from the list"
            options={paperTypes}
          />

          <Box mt={2}>
            <SButton
              fullWidth
              type="submit"
              color="primary"
              variant="contained"
              disabled={!isValid || !dirty}>
              Send
            </SButton>
          </Box>
        </Form>
      )}
    </Formik>
  </SBase>
);

export default LocalPrinterDialog;
